// components/formDialog/DegreeDialog.tsx
import AsyncSingleSelectField from "@/components/form/components/AsyncSingleSelectField";
import InputField from "@/components/form/components/InputField";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { useAddDepartmentProgramMutation, useUpdateDepartmentProgramMutation } from "@/hooks/api/department.queries";
import { masterKey } from "@/hooks/api/master.queries";
import { masterService } from "@/services/master.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

type DegreeDialogProps = {
  mode: "create" | "edit";
  programId?: string;
  programData?: {
    degree_id: string;
    name: string;
    code: string;
  };
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  deptId: string;
  onCancel: () => void;
};

const degreeSchema = z.object({
  degree_id: z.string().min(1, "Degree is required"),
  name: z.string().min(1, "Name is required"),
  code: z.string().min(1, "Code is required"),
});

type DegreeFormType = z.infer<typeof degreeSchema>;

const DegreeDialog = ({
  mode,
  programId,
  programData,
  isOpen,
  onOpenChange,
  deptId,
  onCancel,
}: DegreeDialogProps) => {
  const addMutation = useAddDepartmentProgramMutation(deptId);
  const updateMutation = useUpdateDepartmentProgramMutation(deptId);
  const isPending = addMutation.isPending || updateMutation.isPending;

  const form = useForm<DegreeFormType>({
    resolver: zodResolver(degreeSchema),
    defaultValues: {
      degree_id: "",
      name: "",
      code: "",
    },
  });



  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && programData) {
        form.reset({
          degree_id: programData.degree_id,
          name: programData.name,
          code: programData.code,
        });
      } else {
        form.reset({
          degree_id: "",
          name: "",
          code: "",
        });
      }
    }
  }, [isOpen, mode, programData, form]);

  const handleSubmit = (values: DegreeFormType) => {
    if (mode === "edit" && programId) {
      updateMutation.mutate(
        { programId, data: values },
        {
          onSuccess: () => {
            onCancel();
            form.reset();
          },
        }
      );
    } else {
      addMutation.mutate(
        values,
        {
          onSuccess: () => {
            onCancel();
            form.reset();
          },
        }
      );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === "edit" ? "Update Degree" : "Add Degree to Department"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4 py-4"
          >

            <AsyncSingleSelectField<DegreeFormType>
              name="degree_id"
              label="Degree"
              placeholder="Select Degree"
              fetchOptions={(params) =>
                masterService.fetchCollegeDegreeOptions(deptId, params)
              }
              queryKey={[...masterKey.collegeDegrees(deptId)]}
              disabled={!deptId || mode === "edit"}
              required
            />

            <InputField<DegreeFormType>
              name="name"
              label="Degree Name"
              type="text"
              placeholder="e.g. B.Tech Computer Science"
              required
            />

            <InputField<DegreeFormType>
              name="code"
              label="Degree Code"
              type="text"
              placeholder="e.g. BTECH-CS"
              required
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? mode === "edit"
                    ? "Updating..."
                    : "Adding..."
                  : mode === "edit"
                  ? "Update Degree"
                  : "Add Degree"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default DegreeDialog;
